import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { isAccessEscapeRoute } from './accessRoutes';

/**
 * Whether the app is currently withheld pending payment, and whether the page being viewed is one
 * of the two that stay clear.
 *
 * Polled rather than read once at sign-in: suspending and restoring are things that happen while
 * people are already using the app, and a lock that only appears after a reload is not a lock.
 *
 * Nothing here depends on WHO is asking. Exempting vendor staff by identity meant the person who
 * suspended an installation carried on using it, which is not a suspension.
 */
export function useAccessLock(): { locked: boolean; note: string | null } {
  const { pathname } = useLocation();

  const { data } = useQuery({
    queryKey: ['tenant-access'],
    queryFn: ({ signal }) => api.getTenantAccess(signal),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // A failed poll must not lock anyone out: the server is the authority and refuses writes on
    // its own, so an unreachable API should look like an outage, not like non-payment.
    retry: 1,
  });

  return {
    locked: Boolean(data?.suspended) && !isAccessEscapeRoute(pathname),
    note: data?.note ?? null,
  };
}
